import { PageLayout } from '@/components/PageLayout/PageLayout';
import { Reveal } from '@/components/Reveal/Reveal';
import { DEPARTMENT_PAGE } from '@/dictionary/common';
import { DEPARTMENT_ORDER, DEPARTMENTS } from '@/dictionary/departments';
import { departmentPath, HOME_ANCHORS, ROUTES } from '@/dictionary/routes';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { Button } from '@mantine/core';
import { DEPARTMENT_SLUGS, type DepartmentSlug } from '@shared/department.types';
import { Link, useParams } from 'react-router-dom';
import classes from './DepartmentPage.module.css';

function isDepartmentSlug(value: string | undefined): value is DepartmentSlug {
  return value !== undefined && DEPARTMENT_SLUGS.includes(value as DepartmentSlug);
}

export function DepartmentPage() {
  const { slug } = useParams<{ slug: string }>();
  const department = isDepartmentSlug(slug) ? DEPARTMENTS[slug] : null;

  useDocumentTitle(
    department ? `סינרו | ${department.title}` : DEPARTMENT_PAGE.notFoundTitle,
    department?.tagline,
  );

  if (!department) {
    return (
      <PageLayout>
        <div className={`${classes.wrap} ${classes.notFound}`}>
          <h1 className={classes.title}>{DEPARTMENT_PAGE.notFoundTitle}</h1>
          <p className={classes.tagline}>{DEPARTMENT_PAGE.notFoundBody}</p>
          <Button component={Link} to={ROUTES.home} mt="xl">
            {DEPARTMENT_PAGE.backToHome}
          </Button>
        </div>
      </PageLayout>
    );
  }

  const otherDepartments = DEPARTMENT_ORDER.filter((other) => other !== department.slug);

  return (
    <PageLayout>
      {/* ערכת ה-glass — הגוון הקר של עמודי המחלקות באתר המקורי */}
      <div className={classes.page} data-theme="glass">
        <section className={classes.hero}>
          <div className={classes.wrap}>
            <p className={classes.crumb}>
              <Link to={ROUTES.home} className={classes.crumbLink}>
                {DEPARTMENT_PAGE.backToHome}
              </Link>
              {' · '}
              {department.title}
            </p>
            <div className={classes.heroInner}>
              <div className={classes.heroIcon}>
                <img src={`/assets/icon-${department.slug}.png`} alt="" />
              </div>
              <div>
                <h1 className={classes.title}>{department.title}</h1>
                <p className={classes.tagline}>{department.tagline}</p>
              </div>
            </div>
          </div>
        </section>

        <section className={classes.body}>
          <div className={classes.wrap}>
            <Reveal>
              <div>
                {department.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
                {department.differentiator ? (
                  <p className={classes.differentiator}>
                    <strong>{DEPARTMENT_PAGE.differentiatorLabel}</strong>{' '}
                    {department.differentiator}
                  </p>
                ) : null}
              </div>
            </Reveal>
          </div>
        </section>

        <section>
          <div className={classes.wrap}>
            <Reveal>
              <h2 className={classes.sectionTitle}>{DEPARTMENT_PAGE.servicesTitle}</h2>
            </Reveal>
            <div className={classes.services}>
              {department.services.map((service, index) => (
                <Reveal key={service} delaySeconds={index * 0.05}>
                  <div className={classes.service}>
                    <i className={classes.serviceNumber}>
                      {String(index + 1).padStart(2, '0')}
                    </i>
                    <svg viewBox="0 0 24 24" className={classes.serviceCheck} aria-hidden="true">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                    <span className={classes.serviceLabel}>{service}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className={classes.others}>
          <div className={classes.wrap}>
            <Reveal>
              <h2 className={classes.sectionTitle}>{DEPARTMENT_PAGE.otherDepartmentsTitle}</h2>
            </Reveal>
            <div className={classes.othersGrid}>
              {otherDepartments.map((otherSlug) => {
                const other = DEPARTMENTS[otherSlug];
                const content = (
                  <>
                    <span className={classes.otherIcon}>
                      <img src={`/assets/icon-${otherSlug}.png`} alt="" />
                    </span>
                    <span className={classes.otherTitle}>{other.title}</span>
                  </>
                );

                return other.externalUrl ? (
                  <a
                    key={otherSlug}
                    href={other.externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.other}
                  >
                    {content}
                  </a>
                ) : (
                  <Link key={otherSlug} to={departmentPath(otherSlug)} className={classes.other}>
                    {content}
                  </Link>
                );
              })}
            </div>

            <div className={classes.cta}>
              {department.hasAdvisors ? (
                <Button component={Link} to={ROUTES.advisors} variant="outline">
                  {DEPARTMENT_PAGE.advisorsCta} ←
                </Button>
              ) : null}
              <Button component={Link} to={`${ROUTES.home}${HOME_ANCHORS.contact}`}>
                {department.ctaLabel} ←
              </Button>
            </div>
          </div>
        </section>
      </div>
    </PageLayout>
  );
}
